import { ViewportScroller } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnChanges,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, first, map } from 'rxjs/operators';

import { applicationHomePagesMap } from '@core/domain/constants/application-pages.const';
import { getSectionByIndex } from '@features/category/application/services/category.utils';
import { SeoSelectors, SeoActionTypes } from '@features/seo';
import { checkMatch } from '@frontend/common/utils';
import { routerPublicQueries } from '@frontend/libs/duttirouter';
import { SearchActions } from '@pages/search';
import { HeaderEventAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { MenuCategory } from '@shared/header/models/category.model';
import { CategorySections } from '@shared/header/models/header.models';
import { SetLastSection } from '@shared/menu-categories/store/menu-categories.actions';
import { getLastSectionSelector } from '@shared/menu-categories/store/menu-categories.selectors';
import { GoToHome } from '@shared/navigation';

@Component({
    selector: 'header-sections',
    templateUrl: './header-sections.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
    encapsulation: ViewEncapsulation.None,
})
export class HeaderSectionsComponent implements OnChanges {
    @ViewChild('categoryList') public categoryList: ElementRef;

    @Input() public categories: MenuCategory[];

    @Input() public isCenterHeader = false;

    @Input() public class: string;

    @Input() private section: string;

    @Input() public readonly isSearchPage: boolean;

    public isWomenSection = false;

    public isMenSection = false;

    public lastSection$: Observable<string> = this.store
        .select(getLastSectionSelector)
        .pipe(distinctUntilChanged());

    public homePagesUrl$: Observable<string[]> = this.store.select(SeoSelectors.getHomeUrls)
        .pipe(filter((val) => !!val), first());

    public sections: string[] = CategorySections;

    protected readonly isHomePage$ = this.store.select(routerPublicQueries.getDataFromRouter).pipe(
        map(params => checkMatch(params.pageName, applicationHomePagesMap)),
    );

    @Input() public isNavigateHomeEnabled = false;

    constructor(
        private readonly store: Store,
        private readonly viewportScroller: ViewportScroller,
    ) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.section?.currentValue !== changes.section?.previousValue) {
            this.isWomenSection = this.section === 'WOMEN';
            this.isMenSection = this.section === 'MEN';

            this.viewportScroller.scrollToPosition([0, 0]);
        }
    }

    public selectCategory(event: MouseEvent, index: number): void {
        event.preventDefault();
        const section = getSectionByIndex(index);

        this.store.dispatch(HeaderEventAnalytics({ section: 'category', categoryPath: section }));
        this.store.dispatch(SetLastSection({ section, navigation: true }));
        this.store.dispatch(SeoActionTypes.HomeRouteChange());

        if (this.isNavigateHomeEnabled) {
            this.store.dispatch(HeaderEventAnalytics({ section: 'logo' }));
            this.store.dispatch(GoToHome({}));
            this.viewportScroller.scrollToPosition([0, 0]);
        }
    }

    public trackByIndex(index: number): number {
        return index;
    }

    public changeSection(section: string): void {
        this.viewportScroller.scrollToPosition([0, 0]);

        this.section = section;

        this.setSection(section);
    }

    private setSection(section: string): void {
        this.store.dispatch(SearchActions.SetSectionSearchAction({ section }));
    }
}
